import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { isValidToken, getRefreshToken, setSession, clearSession } from './jwt';

// ----------------------------------------------------------------------

const getBaseUrl = () => {
  const { host, protocol } = window.location;
  return `${protocol}//${host}`;
};

const getApiBaseUrl = () => {
  return getBaseUrl().replace('3000', '8000');
};

const axiosInstance = axios.create({
  baseURL: `${getApiBaseUrl()}/api/v1`,
  timeout: 250000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

// Flag to prevent multiple refresh attempts simultaneously
let isRefreshing = false;
// Queue of failed requests to retry after refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - add auth headers
axiosInstance.interceptors.request.use(
  (config) => {
    const access = localStorage.getItem('access');
    const sessionId = localStorage.getItem('sessionId') || uuidv4();
    const language = localStorage.getItem('language') || 'es';

    // Ensure sessionId is stored
    if (!localStorage.getItem('sessionId')) {
      localStorage.setItem('sessionId', sessionId);
    }

    if (access && isValidToken(access)) {
      config.headers.Authorization = `Bearer ${access}`;
    }

    config.headers['X-Session-Id'] = sessionId;
    config.headers['Accept-Language'] = language;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 and refresh token
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is not 401 or request already retried, reject
    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error.response?.data || 'Something went wrong');
    }

    // Don't try to refresh for token endpoints
    if (originalRequest.url?.includes('/token/')) {
      return Promise.reject(error.response?.data || 'Something went wrong');
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      isRefreshing = false;
      clearSession();
      return Promise.reject(error.response?.data || 'No refresh token');
    }

    try {
      // Call refresh endpoint
      const response = await axios.post(`${getApiBaseUrl()}/api/v1/token/refresh/`, {
        refresh: refreshToken,
      });

      const { access } = response.data;

      // Save new access token (keep existing refresh token)
      setSession(access);

      // Update default header
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`;

      // Process queued requests
      processQueue(null, access);

      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${access}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear session and redirect to login
      processQueue(refreshError, null);
      clearSession();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Legacy helper functions (kept for backward compatibility)
const BearerConfig = (headers = []) => {
  const access = localStorage.getItem('access');
  let sessionId = localStorage.getItem('sessionId');

  if (access && isValidToken(access)) {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`;

    if (headers.length > 0)
      headers.forEach((h) => {
        axiosInstance.defaults.headers[h.head] = h.value;
      });
  } else {
    delete axiosInstance.defaults.headers.common.Authorization;
  }

  if (sessionId) {
    axiosInstance.defaults.headers.common['X-Session-Id'] = sessionId;
  } else {
    sessionId = uuidv4();
    localStorage.setItem('sessionId', sessionId);
    axiosInstance.defaults.headers.common['X-Session-Id'] = sessionId;
  }

  const language = localStorage.getItem('language') || 'es';
  axiosInstance.defaults.headers.common['Accept-Language'] = language;
};

export async function AxiosCall(url) {
  BearerConfig();
  return axiosInstance.get(url);
}

export async function AxiosPost(url, data) {
  BearerConfig();
  return axiosInstance.post(url, data);
}

export async function AxiosFile(url, data) {
  BearerConfig([{ head: 'Content-Type', value: 'multipart/form-data' }]);
  return axiosInstance.post(url, data);
}

export async function AxiosFilePatch(url, data) {
  BearerConfig([{ head: 'Content-Type', value: 'multipart/form-data' }]);
  return axiosInstance.patch(url, data);
}

export async function AxiosPatch(url, data) {
  BearerConfig();
  return axiosInstance.patch(url, data);
}

export async function AxiosDelete(url, data) {
  BearerConfig();
  return axiosInstance.delete(url, data);
}

export async function AxiosDownload(url) {
  BearerConfig();
  return axiosInstance.get(url, { responseType: 'blob' });
}

export default axiosInstance;
